import React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import styled from "styled-components";

import {
  LOCALE_LABELS,
  Locale,
  isTranslatedRoute,
  normalizePathname,
  useLocaleSwitcher,
} from "lib/i18n";

interface LanguageLinkProps {
  locale: Locale;
  className?: string;
  children?: (label: string, isActive: boolean) => React.ReactNode;
}

/**
 * One language option: a real anchor where the alternate exists, a button where
 * it does not.
 *
 * Enabling Next's i18n made every route answer under /es as well, translated or
 * not, so /es/lawyers/states/all returns 200 with the English body. Head.tsx
 * deliberately withholds `hreflang` on those routes -- advertising an alternate
 * that is still English is worse than advertising none -- but an `<a href>` in
 * the footer says the same thing louder, and with `hreflang="es"` on a page that
 * is English it says something false. A crawler following it spends the fetch
 * only to read the `noindex` waiting there.
 *
 * So the anchor is conditional and the check lives here rather than in the two
 * switchers: same reason Head.tsx owns the hreflang rule, a new switcher must not
 * be able to build a link that skips it.
 *
 * Nothing is lost by the button. Switching on an untranslated page still turns
 * the navigation, footer and chrome Spanish and still writes the cookie, so the
 * rest of the visit stays Spanish -- it just cannot be a crawlable claim that a
 * Spanish version of *this* page exists.
 *
 * `router.asPath` carries no locale prefix (measured: the `en` option on
 * /es/about points at /about, not /es/about), so one check covers both locales.
 */
const LanguageLink: React.FC<LanguageLinkProps> = ({
  locale,
  className,
  children,
}) => {
  const router = useRouter();
  const { locale: current, switchTo, rememberLocale } = useLocaleSwitcher();
  const isActive = locale === current;
  const label = LOCALE_LABELS[locale];
  const content = children ? children(label, isActive) : label;

  if (!isTranslatedRoute(normalizePathname(router.asPath))) {
    return (
      <Trigger
        type="button"
        className={className}
        // `lang` describes the label text, which is written in its own language.
        // `hreflang` is not its counterpart and cannot appear here: there is no
        // href to describe.
        lang={locale}
        aria-current={isActive ? "true" : undefined}
        onClick={() => switchTo(locale)}
      >
        {content}
      </Trigger>
    );
  }

  return (
    // `href` comes from Next rather than being assembled here: passing `locale`
    // makes it prefix the path, and `as={asPath}` keeps a dynamic route's params
    // out of the querystring the same way the programmatic switch does.
    <Link
      href={{ pathname: router.pathname, query: router.query }}
      as={router.asPath}
      locale={locale}
      passHref
    >
      <a
        className={className}
        lang={locale}
        hrefLang={locale}
        aria-current={isActive ? "true" : undefined}
        // The anchor navigates; this only has to persist the choice, and it runs
        // before the browser leaves so the next request already carries it.
        onClick={() => rememberLocale(locale)}
      >
        {content}
      </a>
    </Link>
  );
};

/**
 * Both switchers style the inner element they pass as children, so the wrapper
 * only has to stop looking like a button.
 */
const Trigger = styled.button`
  border: 0;
  padding: 0;
  background: none;
  color: inherit;
  font: inherit;
  cursor: pointer;
`;

export default LanguageLink;
